<template>
  <div class="capture-root">
    <div class="topbar">
      <img src="@/assets/logogruas.png" alt="HomePage" class="logo-button" @click="goBack" />
      <div class="topbar-actions">
        <button class="config-btn" @click="openConfig">Configuracion</button>
        <button
          class="weekly-btn"
          :class="{ 'weekly-btn--active': showWeeklyValidation }"
          @click="toggleWeeklyValidation"
        >
          Validacion semanal
        </button>
        <button class="refresh-btn" @click="fetchPeriodData" :disabled="loading">
          {{ loading ? 'Actualizando...' : 'Actualizar' }}
        </button>
      </div>
    </div>

    <header class="header">
      <div>
        <h1>Asistencia de personal</h1>
      </div>
      <div class="header-actions">
        <label class="month-picker">
          Año
          <input type="number" v-model.number="selectedYear" min="2000" max="2100" />
        </label>
        <label class="week-picker">
          Semana (Nomina)
          <select v-model="selectedPayrollWeekKey">
            <option v-for="week in payrollWeeks" :key="week.key" :value="week.key">
              {{ payrollWeekLabel(week) }}
            </option>
          </select>
        </label>
        <button class="download-btn" @click="downloadExcelReport(false)" :disabled="downloadingReport || loading">
          {{ downloadingReport ? 'Generando...' : 'Descargar grupo' }}
        </button>
        <button
          v-if="canDownloadAllGroups"
          class="download-btn"
          @click="downloadExcelReport(true)"
          :disabled="downloadingReport || loading"
        >
          {{ downloadingReport ? 'Generando...' : 'Descargar todos los grupos' }}
        </button>
        <label class="sort-picker">
          Ordenar por
          <select v-model="sortBy">
            <option value="employee_ident">Numero</option>
            <option value="employee_name">Nombre</option>
          </select>
        </label>
        <button class="sort-dir-btn" @click="toggleSortDirection">
          {{ sortDirection === 'asc' ? 'Ascendente' : 'Descendente' }}
        </button>
      </div>
    </header>

    <section v-if="successMessage" class="success-box">{{ successMessage }}</section>
    <section v-if="error" class="error-box">{{ error }}</section>

    <section v-if="attendanceTabs.length" class="attendance-tabs">
      <button
        v-for="tab in attendanceTabs"
        :key="tab.key"
        class="attendance-tab-btn"
        :class="{ 'attendance-tab-btn--active': tab.key === activeAttendanceGroup }"
        @click="activeAttendanceGroup = tab.key"
      >
        {{ tab.label }} ({{ tab.count }})
      </button>
    </section>

    <section v-if="!showWeeklyValidation && sortedActiveGroupRows.length" class="table-wrap">
      <table class="matrix-table">
        <thead>
          <tr>
            <th class="sticky-col">Empleado</th>
            <th
              v-for="(day, idx) in displayDays"
              :key="`h-${day.date}`"
              :class="['day-header', { 'week-start-header': isWeekStartByIndex(idx) }]"
            >
              {{ day.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in sortedActiveGroupRows" :key="row.employee_id">
            <td class="sticky-col sticky-name">
              <div class="name-wrap">
                <button
                  class="name-text name-text--link"
                  title="Ver bitacora de asistencia"
                  @click="openLogbook(row)"
                >
                  {{ row.employee_ident || '---' }} - {{ row.employee_name }}
                </button>
                <div class="employee-actions">
                  <button
                    class="employee-action-btn employee-action-btn--vacation"
                    @click="openRangeStatusModal(row, 'V')"
                  >
                    Programar vacaciones
                  </button>
                  <button
                    class="employee-action-btn employee-action-btn--suspended"
                    @click="openRangeStatusModal(row, 'S')"
                  >
                    Suspender
                  </button>
                </div>
              </div>
            </td>
            <td
              v-for="(value, idx) in row.daily"
              :key="`v-${row.employee_id}-${idx}`"
              :class="{ 'week-start-col': isWeekStartByIndex(idx) }"
            >
              <div class="cell-wrap attendance-cell-editor" @click.stop>
                <div class="attendance-cell-combined">
                  <button
                    class="day-cell"
                    :class="statusClass(value)"
                    @click="openStatusPicker(row.employee_id, idx + 1, $event)"
                  >
                    {{ value || '-' }}
                  </button>
                  <button
                    class="hour-cell"
                    :title="getLocationTooltip(row, idx)"
                    @click="openHoursPicker(row.employee_id, idx + 1, $event)"
                  >
                    <span class="hour-line">
                      <strong>E:</strong>
                      {{ getHourLabel(row, idx, 'clock_in') }}
                    </span>
                    <span class="hour-line">
                      <strong>S:</strong>
                      {{ getHourLabel(row, idx, 'clock_out') }}
                    </span>
                  </button>
                  <button
                    class="extra-cell"
                    @click="openHoursPicker(row.employee_id, idx + 1, $event)"
                  >
                    <span class="hour-line">
                      <strong>Hr. Ext.:</strong>
                      {{ getHourLabel(row, idx, 'extra_hours') }}
                    </span>
                    <span class="hour-line">
                      <strong>Tipo:</strong>
                      {{ getExtraTypeLabel(row, idx) }}
                    </span>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-else-if="showWeeklyValidation && sortedActiveGroupRows.length" class="table-wrap">
      <table class="matrix-table summary-table">
        <thead>
          <tr>
            <th class="sticky-col">Empleado</th>
            <th>Hr. Extra</th>
            <th>Hr. Triple</th>
            <th>Total TE</th>
            <th>Compensacion</th>
            <th>Comisiones</th>
            <th>Taxis</th>
            <th>Retardos</th>
            <th>Faltas</th>
            <th>Permisos</th>
            <th>Complemento de sueldo</th>
            <th>Premios</th>
            <th>Monto turno doble</th>
            <th>Monto turno triple</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="summary in weeklySummaryRows" :key="`sum-${summary.employee_id}`">
            <td class="sticky-col sticky-name">
              <button
                class="name-text name-text--link"
                title="Ver bitacora de asistencia"
                @click="openLogbook(summary)"
              >
                {{ summary.employee_ident || '---' }} - {{ summary.employee_name }}
              </button>
            </td>
            <td>{{ summary.extraHours }}</td>
            <td>{{ summary.tripleHours }}</td>
            <td :class="{ 'summary-error': summary.totalTE === 'Error' }">{{ summary.totalTE }}</td>
            <td :class="{ 'summary-error': summary.compensacion === 'Error' }">{{ summary.compensacion }}</td>
            <td :class="{ 'summary-error': summary.comisiones === 'Error' }">{{ summary.comisiones }}</td>
            <td :class="{ 'summary-error': summary.taxis === 'Error' }">{{ summary.taxis }}</td>
            <td>{{ summary.retardos }}</td>
            <td>{{ summary.faltas }}</td>
            <td>{{ summary.permisos }}</td>
            <td>
              <input
                class="summary-input"
                type="number"
                step="0.01"
                :value="summary.salaryComplement"
                :disabled="savingSalaryComplementId === summary.employee_id"
                @change="onSalaryComplementChange(summary.employee_id, $event.target.value)"
              />
            </td>
            <td :class="{ 'summary-error': summary.premios === 'Error' }">{{ summary.premios }}</td>
            <td :class="{ 'summary-error': summary.montoTurnoDoble === 'Error' }">{{ summary.montoTurnoDoble }}</td>
            <td :class="{ 'summary-error': summary.montoTurnoTriple === 'Error' }">{{ summary.montoTurnoTriple }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section v-else-if="!loading" class="empty-box">
      No hay personal seleccionado o no hay datos en este mes.
    </section>

    <section class="legend">
      <span v-for="opt in legendOptions" :key="opt.code" class="legend-item">
        <span class="legend-pill" :class="statusClass(opt.code)">{{ opt.code }}</span>
        {{ opt.label }}
      </span>
    </section>

    <div
      v-if="editingCell"
      class="status-menu status-menu--floating"
      :style="floatingMenuStyle"
      @click.stop
    >
      <template v-if="editingCell?.mode === 'status'">
        <button
          v-for="opt in statusOptions"
          :key="opt.code"
          class="status-menu__item"
          :class="{ 'status-menu__item--active': editingCellValue === opt.code }"
          @click="updateAttendanceFromEditing(opt.code)"
        >
          <span class="status-menu__dot" :class="statusClass(opt.code)">{{ opt.code }}</span>
          <span>{{ opt.label }}</span>
        </button>
      </template>
      <template v-else>
        <div class="hours-menu">
          <label class="hours-field">
            Entrada
            <input type="time" v-model="hourEditor.clock_in" />
          </label>
          <label class="hours-field">
            Salida
            <input type="time" v-model="hourEditor.clock_out" />
          </label>
          <label class="hours-field">
            Hr. Ext.
            <input
              type="text"
              inputmode="numeric"
              placeholder="HH:mm"
              v-model="hourEditor.extra_hours"
              @blur="hourEditor.extra_hours = normalizeDurationInput(hourEditor.extra_hours)"
            />
          </label>
          <label class="hours-field">
            Tipo
            <select v-model="hourEditor.extra_hours_type">
              <option
                v-for="opt in extraHourTypeOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
          </label>
          <label class="hours-field">
            Compensación
            <input type="number" min="0" step="0.01" v-model="hourEditor.compensation" />
          </label>
          <label class="hours-field hours-field--check">
            <input type="checkbox" v-model="hourEditor.taxi" />
            Taxi
          </label>
          <div class="hours-actions">
            <button class="small-btn" @click="clearHourEditor">Limpiar</button>
            <button class="small-btn" @click="closeStatusPicker">Cancelar</button>
            <button class="small-btn hours-save-btn" :disabled="savingHours" @click="saveHourEditor">
              {{ savingHours ? 'Guardando...' : 'Guardar' }}
            </button>
          </div>
        </div>
      </template>
    </div>

    <transition name="modal-fade">
      <div v-if="showConfigModal" class="modal-overlay" @click.self="closeConfig">
        <div class="modal-card">
          <div class="modal-head">
            <div>
              <h3>Configuracion de personal visible</h3>
              <p class="modal-subtitle">Selecciona los empleados que quieres mostrar en el tablero.</p>
            </div>
            <button class="small-btn" @click="closeConfig">Cerrar</button>
          </div>
          <div class="modal-actions">
            <button class="small-btn" @click="selectAll">Seleccionar todo</button>
            <button class="small-btn" @click="clearAll">Quitar todo</button>
          </div>
          <div class="options-list">
            <label v-for="row in rows" :key="`cfg-${row.employee_id}`" class="option">
              <input
                type="checkbox"
                :checked="selectedEmployees.includes(row.employee_id)"
                @change="toggleEmployee(row.employee_id, $event.target.checked)"
              />
              <span>{{ row.employee_name }}</span>
            </label>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showRangeStatusModal" class="modal-overlay" @click.self="closeRangeStatusModal">
        <div class="modal-card modal-card--compact">
          <div class="modal-head">
            <div>
              <h3>{{ rangeStatusForm.statusLabel }}</h3>
              <p class="modal-subtitle">{{ rangeStatusForm.employeeName }}</p>
            </div>
            <button class="small-btn" @click="closeRangeStatusModal">Cerrar</button>
          </div>
          <div class="range-status-form">
            <label class="hours-field">
              Desde
              <input type="date" v-model="rangeStatusForm.startDate" />
            </label>
            <label class="hours-field">
              Hasta
              <input type="date" v-model="rangeStatusForm.endDate" />
            </label>
            <div class="range-status-actions">
              <button class="small-btn" @click="closeRangeStatusModal">Cancelar</button>
              <button class="small-btn hours-save-btn" :disabled="applyingRangeStatus" @click="applyRangeStatus">
                {{ applyingRangeStatus ? 'Aplicando...' : 'Confirmar' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="logbook-pop">
      <div v-if="showLogbookModal" class="modal-overlay" @click.self="closeLogbook">
        <div class="modal-card logbook-card">
          <div class="modal-head">
            <div>
              <h3>Bitacora de asistencia</h3>
              <p class="modal-subtitle">
                {{ logbook.employeeIdent ? `${logbook.employeeIdent} - ` : '' }}{{ logbook.employeeName }}
              </p>
            </div>
            <button class="small-btn" @click="closeLogbook">Cerrar</button>
          </div>

          <div v-if="logbook.loading" class="logbook-state">Cargando bitacora...</div>
          <div v-else-if="logbook.error" class="logbook-state logbook-state--error">{{ logbook.error }}</div>
          <div v-else-if="!logbook.records.length" class="logbook-state">
            Este empleado no tiene registros de asistencia.
          </div>
          <div v-else class="logbook-body">
            <div class="logbook-list">
              <button
                v-for="entry in logbook.records"
                :key="entry.date"
                class="logbook-entry"
                :class="{ 'logbook-entry--active': selectedLogEntry?.date === entry.date }"
                @click="selectLogEntry(entry)"
              >
                <div class="logbook-entry__top">
                  <span class="logbook-entry__date">{{ formatLogbookDate(entry.date) }}</span>
                  <span class="legend-pill" :class="statusClass(entry.status_code)">{{ entry.status_code }}</span>
                </div>
                <div class="logbook-entry__times">
                  <span><strong>Entrada:</strong> {{ entry.clock_in || '--:--' }}</span>
                  <span><strong>Salida:</strong> {{ entry.clock_out || '--:--' }}</span>
                </div>
                <span
                  class="logbook-entry__source"
                  :class="entryHasLocation(entry) ? 'logbook-entry__source--gps' : 'logbook-entry__source--device'"
                >
                  {{ entryHasLocation(entry) ? 'Con ubicacion' : 'Checador fisico' }}
                </span>
              </button>
              <p v-if="logbook.loadMoreError" class="logbook-load-error">{{ logbook.loadMoreError }}</p>
              <button
                v-if="logbook.hasMore"
                class="logbook-load-more"
                :disabled="logbook.loadingMore"
                @click="loadMoreLogbook"
              >
                {{ logbook.loadingMore ? 'Cargando...' : `Cargar mas (${logbook.records.length} de ${logbook.totalRecords})` }}
              </button>
              <p v-else class="logbook-list-end">
                {{ logbook.records.length }} de {{ logbook.totalRecords }} registros
              </p>
            </div>

            <div class="logbook-detail">
              <template v-if="selectedLogEntry">
                <div class="logbook-detail__head">
                  <h4>{{ formatLogbookDate(selectedLogEntry.date) }}</h4>
                  <span class="logbook-detail__status">{{ selectedLogEntry.status_label }}</span>
                </div>
                <div class="logbook-detail__times">
                  <div class="logbook-time-card">
                    <span>Entrada</span>
                    <strong>{{ selectedLogEntry.clock_in || '--:--' }}</strong>
                  </div>
                  <div class="logbook-time-card">
                    <span>Salida</span>
                    <strong>{{ selectedLogEntry.clock_out || '--:--' }}</strong>
                  </div>
                </div>

                <template v-if="selectedEntryMarkers.length">
                  <LogbookMap
                    class="logbook-map"
                    :center="selectedEntryMapCenter"
                    :zoom="15"
                    map-type-id="roadmap"
                    :options="{ fullscreenControl: false, streetViewControl: false, mapTypeControl: false }"
                  >
                    <LogbookMarker
                      v-for="marker in selectedEntryMarkers"
                      :key="marker.key"
                      :position="marker.position"
                      :label="{ text: marker.label, color: '#ffffff', fontWeight: '700' }"
                      :title="marker.title"
                    />
                  </LogbookMap>
                  <p v-if="selectedEntryLocationText" class="logbook-location-text">
                    {{ selectedEntryLocationText }}
                  </p>
                </template>
                <div v-else-if="entryHasLocation(selectedLogEntry)" class="logbook-no-map">
                  <p class="logbook-location-text">{{ selectedEntryLocationText }}</p>
                  <p class="logbook-no-map__hint">La ubicacion no incluye coordenadas para mostrar en el mapa.</p>
                </div>
                <div v-else class="logbook-device-box">
                  <span class="logbook-device-box__icon">🕐</span>
                  <p>El empleado registro su asistencia en el <strong>Checador fisico</strong>.</p>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import { Map as LogbookMap, Marker as LogbookMarker } from '@fawmi/vue-google-maps'

export default {
  name: 'RHAttendanceDashboard',
  components: {
    LogbookMap,
    LogbookMarker,
  },
  data() {
    const now = new Date()
    return {
      permissions: [],
      selectedYear: now.getFullYear(),
      selectedPayrollWeekKey: '',
      days: [],
      dates: [],
      rows: [],
      selectedEmployees: [],
      loading: false,
      downloadingReport: false,
      error: '',
      successMessage: '',
      sortBy: 'employee_ident',
      sortDirection: 'asc',
      showConfigModal: false,
      showRangeStatusModal: false,
      showLogbookModal: false,
      selectedLogEntry: null,
      logbook: {
        loading: false,
        loadingMore: false,
        error: '',
        loadMoreError: '',
        employeeId: null,
        employeeIdent: '',
        employeeName: '',
        records: [],
        page: 1,
        hasMore: false,
        totalRecords: 0,
      },
      showWeeklyValidation: false,
      savingSalaryComplementId: null,
      activeAttendanceGroup: '',
      editingCell: null,
      savingHours: false,
      applyingRangeStatus: false,
      rangeStatusForm: {
        employeeId: null,
        employeeName: '',
        statusCode: '',
        statusLabel: '',
        startDate: '',
        endDate: '',
      },
      hourEditor: {
        clock_in: '',
        clock_out: '',
        extra_hours: '',
        extra_hours_type: 'normal',
        compensation: '',
        taxi: false,
      },
      floatingMenuStyle: {},
      extraHourTypeOptions: [
        { value: 'normal', label: 'Hr. Ext. Normal' },
        { value: 'triple', label: 'Hora Triple' },
      ],
      statusOptions: [
        { code: '-', label: 'No capturado' },
        { code: 'N', label: 'Turno normal' },
        { code: 'F', label: 'Falta' },
        { code: 'R', label: 'Retraso' },
        { code: 'D', label: 'Turno doble' },
        { code: 'T', label: 'Turno triple' },
        { code: 'S', label: 'Suspendido' },
        { code: 'V', label: 'Vacaciones' },
        { code: 'X', label: 'Descanso' },
        { code: 'P', label: 'Permiso' },
        { code: 'I', label: 'Incapacidad' },
      ],
    }
  },
  computed: {
    canDownloadAllGroups() {
      return this.hasPerm('ui_access_rrhh_attendance_download_all_groups')
    },
    visibleRows() {
      const allowed = new Set(this.selectedEmployees || [])
      return (this.rows || []).filter((row) => allowed.has(row.employee_id))
    },
    payrollWeeks() {
      return this.buildPayrollWeeks(this.selectedYear)
    },
    selectedPayrollWeek() {
      return this.payrollWeeks.find((week) => week.key === this.selectedPayrollWeekKey) || null
    },
    displayDays() {
      if (Array.isArray(this.dates) && this.dates.length) {
        return this.dates.map((dateStr) => ({
          date: dateStr,
          label: this.formatHeaderDate(dateStr),
        }))
      }
      return (this.days || []).map((day) => ({
        date: String(day),
        label: String(day),
      }))
    },
    attendanceTabs() {
      const groups = new Map()
      ;(this.visibleRows || []).forEach((row) => {
        const key = this.normalizeAttendanceGroup(row?.attendance_group)
        const existing = groups.get(key)
        if (existing) {
          existing.count += 1
          return
        }
        groups.set(key, { key, label: key, count: 1 })
      })
      return Array.from(groups.values()).sort((a, b) => a.label.localeCompare(b.label))
    },
    activeGroupRows() {
      const activeGroup = this.activeAttendanceGroup
      if (!activeGroup) return []
      return (this.visibleRows || []).filter(
        (row) => this.normalizeAttendanceGroup(row?.attendance_group) === activeGroup
      )
    },
    sortedActiveGroupRows() {
      const rows = [...(this.activeGroupRows || [])]
      const sortKey = this.sortBy === 'employee_name' ? 'employee_name' : 'employee_ident'
      const direction = this.sortDirection === 'desc' ? -1 : 1
      rows.sort((a, b) => {
        const aValue = String(a?.[sortKey] || '').trim()
        const bValue = String(b?.[sortKey] || '').trim()
        if (sortKey === 'employee_ident') {
          const aNum = Number(aValue)
          const bNum = Number(bValue)
          const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum)
          if (bothNumeric && aNum !== bNum) return (aNum - bNum) * direction
        }
        return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' }) * direction
      })
      return rows
    },
    legendOptions() {
      return this.statusOptions
    },
    editingCellValue() {
      if (!this.editingCell) return '-'
      const row = this.rows.find((item) => item.employee_id === this.editingCell.employeeId)
      if (!row) return '-'
      return (row.daily || [])[this.editingCell.day - 1] || '-'
    },
    weeklySummaryRows() {
      return (this.sortedActiveGroupRows || []).map((row) => {
        const daily = Array.isArray(row?.daily) ? row.daily : []
        const dailyHours = Array.isArray(row?.daily_hours) ? row.daily_hours : []
        let extraMinutes = 0
        let tripleMinutes = 0
        let taxiDays = 0
        dailyHours.forEach((entry) => {
          const minutes = this.parseHoursToMinutes(entry?.extra_hours)
          if (minutes) {
            if (String(entry?.extra_hours_type || '').trim() === 'triple') {
              tripleMinutes += minutes
            } else {
              extraMinutes += minutes
            }
          }
          if (entry?.taxi) taxiDays += 1
        })
        const countStatus = (code) =>
          daily.filter((value) => String(value || '').toUpperCase() === code).length
        const dDays = countStatus('D')
        const tDays = countStatus('T')
        const rDays = countStatus('R')
        const fDays = countStatus('F')
        const sDays = countStatus('S')
        const pDays = countStatus('P')
        const iDays = countStatus('I')
        return {
          employee_id: row.employee_id,
          employee_ident: row.employee_ident,
          employee_name: row.employee_name,
          extraHours: this.formatMinutesToHours(extraMinutes),
          tripleHours: this.formatMinutesToHours(tripleMinutes),
          totalTE: this.computeMoneyCell(
            () =>
              (extraMinutes / 60) * this.parseNumberStrict(row?.extra_hours_value) +
              (tripleMinutes / 60) * this.parseNumberStrict(row?.triple_extra_hours_value)
          ),
          compensacion: this.computeMoneyCell(() =>
            dailyHours.reduce((sum, entry) => sum + this.parseNumberStrict(entry?.compensation), 0)
          ),
          comisiones: this.computeMoneyCell(() => this.parseNumberStrict(row?.commission)),
          taxis: this.computeMoneyCell(() => taxiDays * this.parseNumberStrict(row?.taxi_allowance)),
          retardos: rDays,
          faltas: fDays + sDays + iDays,
          permisos: pDays,
          salaryComplement:
            row?.salary_complement === null || row?.salary_complement === undefined
              ? ''
              : String(row.salary_complement),
          premios: this.computeMoneyCell(() => {
            const weeklyIncentive = this.parseNumberStrict(row?.weekly_incentive_value)
            if (fDays > 0 || sDays > 0 || iDays > 0 || rDays >= 3) return 0
            return weeklyIncentive - rDays * (weeklyIncentive / 7)
          }),
          montoTurnoDoble: this.computeMoneyCell(
            () => dDays * this.parseNumberStrict(row?.double_shifts_value)
          ),
          montoTurnoTriple: this.computeMoneyCell(
            () => tDays * this.parseNumberStrict(row?.triple_shifts_value)
          ),
        }
      })
    },
    selectedEntryMarkers() {
      const entry = this.selectedLogEntry
      if (!entry) return []
      const markers = []
      const clockInCoords = this.parseCoordinates(entry.clock_in_location)
      if (clockInCoords) {
        markers.push({
          key: 'clock-in',
          position: clockInCoords,
          label: 'E',
          title: `Entrada ${entry.clock_in || ''}`.trim(),
        })
      }
      const clockOutCoords = this.parseCoordinates(entry.clock_out_location)
      if (clockOutCoords) {
        markers.push({
          key: 'clock-out',
          position: clockOutCoords,
          label: 'S',
          title: `Salida ${entry.clock_out || ''}`.trim(),
        })
      }
      return markers
    },
    selectedEntryMapCenter() {
      const markers = this.selectedEntryMarkers
      if (!markers.length) return { lat: 25.6866, lng: -100.3161 }
      const lat = markers.reduce((sum, marker) => sum + marker.position.lat, 0) / markers.length
      const lng = markers.reduce((sum, marker) => sum + marker.position.lng, 0) / markers.length
      return { lat, lng }
    },
    selectedEntryLocationText() {
      const entry = this.selectedLogEntry
      if (!entry) return ''
      const lines = []
      if (entry.clock_in_location) lines.push(`Entrada: ${entry.clock_in_location}`)
      if (entry.clock_out_location) lines.push(`Salida: ${entry.clock_out_location}`)
      return lines.join(' · ')
    },
  },
  watch: {
    attendanceTabs: {
      immediate: true,
      handler(tabs) {
        if (!tabs.length) {
          this.activeAttendanceGroup = ''
          return
        }
        const isActiveValid = tabs.some((tab) => tab.key === this.activeAttendanceGroup)
        if (!isActiveValid) {
          this.activeAttendanceGroup = tabs[0].key
        }
      },
    },
    selectedYear() {
      this.syncWeekSelectionForYear()
    },
    selectedPayrollWeekKey() {
      this.fetchPeriodData()
    },
  },
  created() {
    this.fetchCurrentUser()
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocumentClick)
  },
  methods: {
    hasPerm(perm) {
      if (!perm) return false
      if (this.permissions.includes(perm)) return true
      return this.permissions.includes(`access_control.${perm}`)
    },
    hasAllPerms(perms) {
      return Array.isArray(perms) && perms.length > 0 && perms.every((perm) => this.hasPerm(perm))
    },
    apiBase() {
      return process.env.API_DIRECTORY || ''
    },
    authHeaders() {
      return { Authorization: `Token ${localStorage.getItem('authToken')}` }
    },
    getTargetDateByDayIndex(day) {
      const idx = Number(day) - 1
      const fromDates = (this.dates || [])[idx]
      if (fromDates) return fromDates
      if (this.selectedPayrollWeek) {
        const base = new Date(`${this.selectedPayrollWeek.startDate}T00:00:00`)
        base.setDate(base.getDate() + idx)
        return this.toIsoDate(base)
      }
      return ''
    },
    async fetchCurrentUser() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const { data } = await axios.get(`${apiDirectory}/current-user/`, {
          headers: this.authHeaders(),
        })
        this.permissions = data.permissions || []
        if (!this.hasAllPerms(['ui_access_rrhh_attendance', 'ui_access_rrhh_module'])) {
          this.$router.push({ name: 'MainPanel' })
          return
        }
        this.syncWeekSelectionForYear()
      } catch (error) {
        this.$router.push({ name: 'MainPanel' })
      }
    },
    syncWeekSelectionForYear() {
      const weeks = this.payrollWeeks
      if (!weeks.length) {
        this.selectedPayrollWeekKey = ''
        return
      }
      const stillValid = weeks.some((week) => week.key === this.selectedPayrollWeekKey)
      if (!stillValid) {
        this.selectedPayrollWeekKey = weeks[0].key
      }
    },
    buildPayrollWeeks(year) {
      const parsedYear = Number(year)
      if (!Number.isInteger(parsedYear) || parsedYear < 1900) return []
      const startOfYear = new Date(parsedYear, 0, 1)
      const endOfYear = new Date(parsedYear, 11, 31)
      // Week 1 is the Tuesday-Monday week containing January 1; a week
      // belongs to the year its Monday (end date) falls in.
      const offsetBackToTuesday = (startOfYear.getDay() - 2 + 7) % 7
      const weeks = []
      let cursor = new Date(parsedYear, 0, 1 - offsetBackToTuesday)
      let index = 1
      for (;;) {
        const start = new Date(cursor)
        const end = new Date(cursor)
        end.setDate(end.getDate() + 6)
        if (end > endOfYear) break
        const startDate = this.toIsoDate(start)
        const endDate = this.toIsoDate(end)
        weeks.push({
          key: `${parsedYear}-${String(index).padStart(2, '0')}`,
          index,
          startDate,
          endDate,
        })
        cursor.setDate(cursor.getDate() + 7)
        index += 1
      }
      return weeks
    },
    payrollWeekLabel(week) {
      if (!week) return ''
      return `Semana ${week.index} (${this.formatShortDate(week.startDate)} - ${this.formatShortDate(week.endDate)})`
    },
    toIsoDate(value) {
      const year = value.getFullYear()
      const month = String(value.getMonth() + 1).padStart(2, '0')
      const day = String(value.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    formatShortDate(dateStr) {
      if (!dateStr) return ''
      const [year, month, day] = dateStr.split('-')
      return `${day}/${month}/${year}`
    },
    formatHeaderDate(dateStr) {
      if (!dateStr) return ''
      const parsed = new Date(`${dateStr}T00:00:00`)
      const weekNames = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab']
      const dayName = weekNames[parsed.getDay()] || ''
      const [, month, day] = dateStr.split('-')
      return `${dayName} ${day}/${month}`
    },
    async fetchPeriodData() {
      if (!this.selectedPayrollWeek) return
      this.loading = true
      this.error = ''
      try {
        const { data } = await axios.get(`${this.apiBase()}/dashboard/rrhh-attendance-monthly-matrix`, {
          params: {
            start_date: this.selectedPayrollWeek.startDate,
            end_date: this.selectedPayrollWeek.endDate,
          },
          headers: this.authHeaders(),
        })
        this.days = Array.isArray(data?.days) ? data.days : []
        this.dates = Array.isArray(data?.dates) ? data.dates : []
        this.rows = Array.isArray(data?.series)
          ? data.series.map((row) => ({
              ...row,
              daily_hours: this.normalizeDailyHours(row?.daily_hours, row?.daily),
            }))
          : []
        await this.fetchConfig()
      } catch (error) {
        this.error = 'No se pudo cargar el tablero de asistencia.'
      } finally {
        this.loading = false
      }
    },
    async fetchConfig() {
      try {
        const { data } = await axios.get(`${this.apiBase()}/dashboard/rrhh-attendance-config`, {
          headers: this.authHeaders(),
        })
        const fromApi = Array.isArray(data?.visible_employee_ids) ? data.visible_employee_ids : []
        this.selectedEmployees = data?.is_configured ? fromApi : (this.rows || []).map((row) => row.employee_id)
      } catch (error) {
        this.selectedEmployees = (this.rows || []).map((row) => row.employee_id)
      }
      this.syncEmployeeSelection()
    },
    syncEmployeeSelection() {
      const validIds = new Set((this.rows || []).map((row) => row.employee_id))
      this.selectedEmployees = (this.selectedEmployees || []).filter((id) => validIds.has(id))
    },
    openConfig() {
      this.showConfigModal = true
    },
    closeConfig() {
      this.showConfigModal = false
    },
    async openLogbook(row) {
      this.closeStatusPicker()
      this.selectedLogEntry = null
      this.logbook = {
        loading: true,
        loadingMore: false,
        error: '',
        loadMoreError: '',
        employeeId: row?.employee_id || null,
        employeeIdent: String(row?.employee_ident || ''),
        employeeName: String(row?.employee_name || ''),
        records: [],
        page: 1,
        hasMore: false,
        totalRecords: 0,
      }
      this.showLogbookModal = true
      try {
        const data = await this.fetchLogbookPage(1)
        const records = Array.isArray(data?.records) ? data.records : []
        this.logbook = {
          ...this.logbook,
          loading: false,
          employeeIdent: String(data?.employee_ident || this.logbook.employeeIdent),
          employeeName: String(data?.employee_name || this.logbook.employeeName),
          records,
          page: Number(data?.page) || 1,
          hasMore: Boolean(data?.has_more),
          totalRecords: Number(data?.total_records) || records.length,
        }
        this.selectedLogEntry = records[0] || null
      } catch (error) {
        this.logbook = {
          ...this.logbook,
          loading: false,
          error: 'No se pudo cargar la bitacora de asistencia.',
        }
      }
    },
    async fetchLogbookPage(page) {
      const { data } = await axios.get(`${this.apiBase()}/dashboard/rrhh-attendance-employee-logbook`, {
        params: { employee_id: this.logbook.employeeId, page, page_size: 30 },
        headers: this.authHeaders(),
      })
      return data
    },
    async loadMoreLogbook() {
      if (this.logbook.loadingMore || !this.logbook.hasMore) return
      const requestedEmployeeId = this.logbook.employeeId
      this.logbook = { ...this.logbook, loadingMore: true, loadMoreError: '' }
      try {
        const data = await this.fetchLogbookPage(this.logbook.page + 1)
        // The modal may have been reopened for someone else while this request ran.
        if (this.logbook.employeeId !== requestedEmployeeId) return
        const newRecords = Array.isArray(data?.records) ? data.records : []
        this.logbook = {
          ...this.logbook,
          loadingMore: false,
          records: [...this.logbook.records, ...newRecords],
          page: Number(data?.page) || this.logbook.page + 1,
          hasMore: Boolean(data?.has_more),
          totalRecords: Number(data?.total_records) || this.logbook.totalRecords,
        }
      } catch (error) {
        if (this.logbook.employeeId !== requestedEmployeeId) return
        this.logbook = {
          ...this.logbook,
          loadingMore: false,
          loadMoreError: 'No se pudieron cargar mas registros.',
        }
      }
    },
    closeLogbook() {
      this.showLogbookModal = false
      this.selectedLogEntry = null
    },
    selectLogEntry(entry) {
      this.selectedLogEntry = entry
    },
    entryHasLocation(entry) {
      return Boolean(
        String(entry?.clock_in_location || '').trim() || String(entry?.clock_out_location || '').trim()
      )
    },
    parseCoordinates(text) {
      const match = String(text || '').match(/(-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/)
      if (!match) return null
      const lat = Number(match[1])
      const lng = Number(match[2])
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
      return { lat, lng }
    },
    formatLogbookDate(dateStr) {
      if (!dateStr) return ''
      const parsed = new Date(`${dateStr}T00:00:00`)
      if (Number.isNaN(parsed.getTime())) return dateStr
      const weekNames = ['Domingo', 'Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado']
      return `${weekNames[parsed.getDay()]} ${this.formatShortDate(dateStr)}`
    },
    openRangeStatusModal(row, statusCode) {
      this.closeStatusPicker()
      const statusMap = {
        V: 'Programar vacaciones',
        S: 'Suspender',
      }
      const firstDate = String((this.dates || [])[0] || this.selectedPayrollWeek?.startDate || '')
      const lastDate = String(
        (this.dates || [])[this.dates.length - 1] || this.selectedPayrollWeek?.endDate || firstDate
      )
      this.rangeStatusForm = {
        employeeId: row?.employee_id || null,
        employeeName: row?.employee_name || '',
        statusCode: statusCode === 'S' ? 'S' : 'V',
        statusLabel: statusMap[statusCode === 'S' ? 'S' : 'V'],
        startDate: firstDate,
        endDate: lastDate,
      }
      this.showRangeStatusModal = true
    },
    closeRangeStatusModal() {
      this.showRangeStatusModal = false
      this.rangeStatusForm = {
        employeeId: null,
        employeeName: '',
        statusCode: '',
        statusLabel: '',
        startDate: '',
        endDate: '',
      }
    },
    buildIsoDateRange(startDate, endDate) {
      const result = []
      if (!startDate || !endDate) return result
      const start = new Date(`${startDate}T00:00:00`)
      const end = new Date(`${endDate}T00:00:00`)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return result
      const cursor = new Date(start)
      while (cursor <= end) {
        result.push(this.toIsoDate(cursor))
        cursor.setDate(cursor.getDate() + 1)
      }
      return result
    },
    async applyRangeStatus() {
      if (this.applyingRangeStatus) return
      this.successMessage = ''
      const employeeId = Number(this.rangeStatusForm.employeeId || 0)
      const startDate = String(this.rangeStatusForm.startDate || '').trim()
      const endDate = String(this.rangeStatusForm.endDate || '').trim()
      const statusCode = String(this.rangeStatusForm.statusCode || '').trim().toUpperCase()
      if (!employeeId || !startDate || !endDate || !statusCode) {
        this.error = 'Selecciona empleado, rango de fechas y estatus.'
        return
      }
      const targetDates = this.buildIsoDateRange(startDate, endDate)
      if (!targetDates.length) {
        this.error = 'El rango de fechas no es valido.'
        return
      }
      if (targetDates.length > 93) {
        this.error = 'El rango maximo permitido es de 93 dias.'
        return
      }
      this.applyingRangeStatus = true
      this.error = ''
      try {
        for (const targetDate of targetDates) {
          await axios.put(
            `${this.apiBase()}/dashboard/rrhh-attendance-day-status`,
            {
              employee_id: employeeId,
              date: targetDate,
              status_code: statusCode,
            },
            { headers: this.authHeaders() }
          )
        }
        await this.fetchPeriodData()
        this.successMessage = `Se aplicó ${this.rangeStatusForm.statusLabel.toLowerCase()} del ${startDate} al ${endDate}.`
        this.applyingRangeStatus = false
        this.closeRangeStatusModal()
      } catch (error) {
        this.error = 'No se pudo aplicar el estatus al rango seleccionado.'
      } finally {
        this.applyingRangeStatus = false
      }
    },
    toggleEmployee(employeeId, checked) {
      if (checked) {
        if (!this.selectedEmployees.includes(employeeId)) {
          this.selectedEmployees = [...this.selectedEmployees, employeeId]
        }
      } else {
        this.selectedEmployees = this.selectedEmployees.filter((id) => id !== employeeId)
      }
      this.saveConfig()
    },
    selectAll() {
      this.selectedEmployees = (this.rows || []).map((row) => row.employee_id)
      this.saveConfig()
    },
    clearAll() {
      this.selectedEmployees = []
      this.saveConfig()
    },
    toggleSortDirection() {
      this.sortDirection = this.sortDirection === 'desc' ? 'asc' : 'desc'
    },
    toggleWeeklyValidation() {
      this.closeStatusPicker()
      this.showWeeklyValidation = !this.showWeeklyValidation
    },
    async onSalaryComplementChange(employeeId, value) {
      const weekStart = this.selectedPayrollWeek?.startDate
      if (!weekStart) return
      const normalized = String(value ?? '').trim()
      this.error = ''
      this.savingSalaryComplementId = employeeId
      this.rows = this.rows.map((item) =>
        item.employee_id === employeeId ? { ...item, salary_complement: normalized } : item
      )
      try {
        const { data } = await axios.put(
          `${this.apiBase()}/dashboard/rrhh-attendance-weekly-payroll`,
          {
            employee_id: employeeId,
            week_start_date: weekStart,
            salary_complement: normalized,
          },
          { headers: this.authHeaders() }
        )
        const saved = data?.salary_complement
        this.rows = this.rows.map((item) =>
          item.employee_id === employeeId
            ? { ...item, salary_complement: saved === null || saved === undefined ? '' : String(saved) }
            : item
        )
      } catch (error) {
        this.error = 'No se pudo guardar el complemento de sueldo.'
      } finally {
        this.savingSalaryComplementId = null
      }
    },
    parseHoursToMinutes(value) {
      const text = String(value || '').trim()
      if (!text) return 0
      const [hoursPart, minutesPart] = text.split(':')
      const hours = Number(hoursPart)
      const minutes = Number(minutesPart)
      if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0
      return hours * 60 + minutes
    },
    formatMinutesToHours(totalMinutes) {
      const safeMinutes = Math.max(0, Math.round(Number(totalMinutes) || 0))
      const hours = Math.floor(safeMinutes / 60)
      const minutes = safeMinutes % 60
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    },
    formatMoney(value) {
      const amount = Number(value) || 0
      return `$${amount.toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    },
    parseNumberStrict(value) {
      if (value === null || value === undefined || String(value).trim() === '') return 0
      const amount = Number(value)
      if (!Number.isFinite(amount)) throw new Error('Valor no numerico')
      return amount
    },
    computeMoneyCell(calculate) {
      try {
        return this.formatMoney(calculate())
      } catch (error) {
        return 'Error'
      }
    },
    async saveConfig() {
      try {
        await axios.put(
          `${this.apiBase()}/dashboard/rrhh-attendance-config`,
          { visible_employee_ids: this.selectedEmployees || [] },
          { headers: this.authHeaders() }
        )
      } catch (error) {
        this.error = 'No se pudo guardar la configuracion de personal.'
      }
    },
    isWeekStartByIndex(idx) {
      const n = Number(idx || 0)
      return n % 7 === 0
    },
    statusClass(code) {
      const value = String(code || '-').toUpperCase()
      if (value === 'N' || value === 'A') return 'status-a'
      if (value === 'F') return 'status-f'
      if (value === 'R') return 'status-r'
      if (value === 'D') return 'status-d'
      if (value === 'T') return 'status-t'
      if (value === 'S') return 'status-s'
      if (value === 'V' || value === 'X') return 'status-v'
      if (value === 'P') return 'status-p'
      if (value === 'I') return 'status-i'
      return 'status-empty'
    },
    normalizeAttendanceGroup(group) {
      const value = String(group || '').trim()
      return value || 'Sin grupo'
    },
    normalizeDailyHours(dailyHours, daily) {
      const statusList = Array.isArray(daily) ? daily : []
      const result = statusList.map(() => ({
        clock_in: '',
        clock_out: '',
        clock_in_location: '',
        clock_out_location: '',
        extra_hours: '',
        extra_hours_type: '',
        compensation: '',
        taxi: false,
      }))
      if (!Array.isArray(dailyHours)) return result
      return result.map((_, idx) => {
        const source = dailyHours[idx] || {}
        return {
          clock_in: String(source.clock_in || '').slice(0, 5),
          clock_out: String(source.clock_out || '').slice(0, 5),
          clock_in_location: String(source.clock_in_location || ''),
          clock_out_location: String(source.clock_out_location || ''),
          extra_hours: String(source.extra_hours || '').slice(0, 5),
          extra_hours_type: String(source.extra_hours_type || '').trim(),
          compensation: source.compensation === null || source.compensation === undefined
            ? ''
            : String(source.compensation),
          taxi: Boolean(source.taxi),
        }
      })
    },
    isEditingCell(employeeId, day, mode) {
      return (
        this.editingCell?.employeeId === employeeId &&
        this.editingCell?.day === day &&
        this.editingCell?.mode === mode
      )
    },
    positionFloatingMenu(event, options = {}) {
      const anchorRect = event?.currentTarget?.getBoundingClientRect()
      const menuWidth = options.width || 190
      const menuHeight = options.height || 228
      const spacing = 8
      let left = anchorRect?.left || 0
      let top = (anchorRect?.bottom || 0) + spacing

      const maxLeft = window.innerWidth - menuWidth - spacing
      if (left > maxLeft) left = Math.max(spacing, maxLeft)
      if (left < spacing) left = spacing
      if (top + menuHeight > window.innerHeight - spacing) {
        top = Math.max(spacing, (anchorRect?.top || 0) - menuHeight - spacing)
      }
      this.floatingMenuStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
      }
    },
    openStatusPicker(employeeId, day, event) {
      if (this.isEditingCell(employeeId, day, 'status')) {
        this.closeStatusPicker()
        return
      }
      this.positionFloatingMenu(event, { width: 190, height: 228 })
      this.hourEditor = { clock_in: '', clock_out: '', extra_hours: '', extra_hours_type: 'normal', compensation: '', taxi: false }
      this.editingCell = { employeeId, day, mode: 'status' }
    },
    openHoursPicker(employeeId, day, event) {
      if (this.isEditingCell(employeeId, day, 'hours')) {
        this.closeStatusPicker()
        return
      }
      this.positionFloatingMenu(event, { width: 270, height: 300 })
      const row = this.rows.find((item) => item.employee_id === employeeId)
      const dayHours = ((row?.daily_hours || [])[day - 1]) || {}
      this.hourEditor = {
        clock_in: String(dayHours.clock_in || '').slice(0, 5),
        clock_out: String(dayHours.clock_out || '').slice(0, 5),
        extra_hours: String(dayHours.extra_hours || '').slice(0, 5),
        extra_hours_type: String(dayHours.extra_hours_type || '').trim() || 'normal',
        compensation: dayHours.compensation === null || dayHours.compensation === undefined
          ? ''
          : String(dayHours.compensation),
        taxi: Boolean(dayHours.taxi),
      }
      this.editingCell = { employeeId, day, mode: 'hours' }
    },
    closeStatusPicker() {
      this.editingCell = null
      this.hourEditor = { clock_in: '', clock_out: '', extra_hours: '', extra_hours_type: 'normal', compensation: '', taxi: false }
    },
    onDocumentClick() {
      if (this.editingCell) this.closeStatusPicker()
    },
    updateAttendanceFromEditing(statusCode) {
      if (!this.editingCell) return
      const row = this.rows.find((item) => item.employee_id === this.editingCell.employeeId)
      if (!row) {
        this.closeStatusPicker()
        return
      }
      this.updateAttendance(row, this.editingCell.day, statusCode)
    },
    getHourLabel(row, idx, key) {
      const value = (row?.daily_hours || [])[idx]?.[key]
      const text = String(value || '').trim()
      return text || '--:--'
    },
    getLocationTooltip(row, idx) {
      const entry = (row?.daily_hours || [])[idx] || {}
      const lines = []
      if (entry.clock_in_location) lines.push(`Entrada: ${entry.clock_in_location}`)
      if (entry.clock_out_location) lines.push(`Salida: ${entry.clock_out_location}`)
      return lines.join('\n')
    },
    getExtraTypeLabel(row, idx) {
      const value = String((row?.daily_hours || [])[idx]?.extra_hours_type || '').trim()
      if (value === 'normal') return 'Hr. Ext. Normal'
      if (value === 'triple') return 'Hora Triple'
      return '--'
    },
    clearHourEditor() {
      this.hourEditor = { clock_in: '', clock_out: '', extra_hours: '', extra_hours_type: 'normal', compensation: '', taxi: false }
    },
    async saveHourEditor() {
      if (!this.editingCell || this.savingHours) return
      const row = this.rows.find((item) => item.employee_id === this.editingCell.employeeId)
      if (!row) {
        this.closeStatusPicker()
        return
      }
      const clockIn = String(this.hourEditor.clock_in || '').slice(0, 5)
      const clockOut = String(this.hourEditor.clock_out || '').slice(0, 5)
      const extraHours = this.normalizeDurationInput(this.hourEditor.extra_hours)
      const extraHoursType = extraHours
        ? String(this.hourEditor.extra_hours_type || '').trim() || 'normal'
        : ''
      const compensation = String(this.hourEditor.compensation ?? '').trim()
      const taxi = Boolean(this.hourEditor.taxi)
      await this.updateAttendanceHours(
        row,
        this.editingCell.day,
        clockIn,
        clockOut,
        extraHours,
        extraHoursType,
        compensation,
        taxi
      )
    },
    normalizeDurationInput(value) {
      const text = String(value ?? '').trim()
      if (!text) return ''
      let hours = 0
      let minutes = 0
      if (text.includes(':')) {
        const [hoursPart, minutesPart] = text.split(':')
        hours = parseInt(hoursPart, 10) || 0
        minutes = parseInt(minutesPart, 10) || 0
      } else {
        hours = parseInt(text, 10) || 0
      }
      if (minutes > 59) minutes = 59
      if (minutes < 0) minutes = 0
      if (hours > 23) hours = 23
      if (hours < 0) hours = 0
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    },
    async updateAttendance(row, day, statusCode) {
      const targetDate = this.getTargetDateByDayIndex(day)
      if (!targetDate) return
      try {
        await axios.put(
          `${this.apiBase()}/dashboard/rrhh-attendance-day-status`,
          {
            employee_id: row.employee_id,
            date: targetDate,
            status_code: statusCode,
          },
          { headers: this.authHeaders() }
        )
        const updatedRows = this.rows.map((item) => {
          if (item.employee_id !== row.employee_id) return item
          const daily = [...(item.daily || [])]
          daily[day - 1] = statusCode
          return { ...item, daily }
        })
        this.rows = updatedRows
      } catch (error) {
        this.error = 'No se pudo actualizar la asistencia del día.'
      } finally {
        this.closeStatusPicker()
      }
    },
    async updateAttendanceHours(row, day, clockIn, clockOut, extraHours, extraHoursType, compensation, taxi) {
      const targetDate = this.getTargetDateByDayIndex(day)
      if (!targetDate) return
      this.savingHours = true
      try {
        const { data } = await axios.put(
          `${this.apiBase()}/dashboard/rrhh-attendance-day-status`,
          {
            employee_id: row.employee_id,
            date: targetDate,
            clock_in: clockIn || '',
            clock_out: clockOut || '',
            extra_hours: extraHours || '',
            extra_hours_type: extraHoursType || '',
            compensation: compensation || '',
            taxi: Boolean(taxi),
          },
          { headers: this.authHeaders() }
        )
        const updatedRows = this.rows.map((item) => {
          if (item.employee_id !== row.employee_id) return item
          const daily = [...(item.daily || [])]
          const dailyHours = this.normalizeDailyHours(item?.daily_hours, item?.daily)
          daily[day - 1] = data?.status_code || daily[day - 1] || '-'
          dailyHours[day - 1] = {
            clock_in: String(data?.clock_in || '').slice(0, 5),
            clock_out: String(data?.clock_out || '').slice(0, 5),
            clock_in_location: String(data?.clock_in_location || ''),
            clock_out_location: String(data?.clock_out_location || ''),
            extra_hours: String(data?.extra_hours || '').slice(0, 5),
            extra_hours_type: String(data?.extra_hours_type || '').trim(),
            compensation: data?.compensation === null || data?.compensation === undefined
              ? ''
              : String(data.compensation),
            taxi: Boolean(data?.taxi),
          }
          return { ...item, daily, daily_hours: dailyHours }
        })
        this.rows = updatedRows
      } catch (error) {
        this.error = 'No se pudo actualizar la hora de asistencia.'
      } finally {
        this.savingHours = false
        this.closeStatusPicker()
      }
    },
    async downloadExcelReport(allGroups = false) {
      this.error = ''
      const startDate = this.selectedPayrollWeek?.startDate || ''
      const endDate = this.selectedPayrollWeek?.endDate || ''
      if (!startDate || !endDate) {
        this.error = 'Selecciona una semana para el reporte.'
        return
      }

      this.downloadingReport = true
      try {
        const params = {
          start_date: startDate,
          end_date: endDate,
        }
        if (!allGroups && this.activeAttendanceGroup) {
          params.attendance_group = this.activeAttendanceGroup
        }
        const response = await axios.get(`${this.apiBase()}/dashboard/rrhh-attendance-export-matrix`, {
          params,
          headers: this.authHeaders(),
          responseType: 'blob',
        })
        const disposition = response.headers?.['content-disposition'] || ''
        const match = disposition.match(/filename="?([^"]+)"?/)
        const fileName = match?.[1] || `asistencia-${startDate}-a-${endDate}.xlsx`
        const blob = new Blob(
          [response.data],
          { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
        )
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        this.error = 'No se pudo generar el reporte de asistencia.'
      } finally {
        this.downloadingReport = false
      }
    },
    statusBg(code) {
      const value = String(code || '-').toUpperCase()
      if (value === 'N' || value === 'A') return '#dcfce7'
      if (value === 'F') return '#fee2e2'
      if (value === 'R') return '#fef9c3'
      if (value === 'D') return '#dbeafe'
      if (value === 'T') return '#ede9fe'
      if (value === 'S') return '#fecaca'
      if (value === 'V' || value === 'X') return '#ecfccb'
      if (value === 'P') return '#cffafe'
      if (value === 'I') return '#fed7aa'
      return '#ffffff'
    },
    statusColor(code) {
      const value = String(code || '-').toUpperCase()
      if (value === 'N' || value === 'A') return '#166534'
      if (value === 'F') return '#991b1b'
      if (value === 'R') return '#854d0e'
      if (value === 'D') return '#1e40af'
      if (value === 'T') return '#5b21b6'
      if (value === 'S') return '#7f1d1d'
      if (value === 'V' || value === 'X') return '#3f6212'
      if (value === 'P') return '#0e7490'
      if (value === 'I') return '#9a3412'
      return '#334155'
    },
    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    goBack() {
      this.$router.push({ name: 'RHModule' })
    },
  },
}
</script>

<style scoped>
.capture-root {
  min-height: 100vh;
  background:
    radial-gradient(circle at 5% 0%, rgba(34, 197, 94, 0.14), transparent 35%),
    radial-gradient(circle at 92% 2%, rgba(124, 58, 237, 0.14), transparent 32%),
    #eef3fb;
  padding: 76px 16px 24px;
}
.topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  background: #ffc434;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  z-index: 20;
}
.logo-button {
  height: 65%;
  object-fit: contain;
  cursor: pointer;
}
.topbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.config-btn {
  border: 1px solid #0f172a;
  background: #fff;
  color: #0f172a;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
}
.weekly-btn {
  border: 1px solid #0f172a;
  background: #fff;
  color: #0f172a;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 600;
}
.weekly-btn--active {
  background: #1d4ed8;
  border-color: #1d4ed8;
  color: #fff;
}
.refresh-btn {
  border: none;
  background: #111827;
  color: #fff;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
}
.header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.76);
  border: 1px solid rgba(148, 163, 184, 0.26);
  border-radius: 14px;
  padding: 8px 12px;
  box-shadow: 0 10px 22px rgba(15, 23, 42, 0.07);
}
.header h1 {
  margin: 0;
  color: #0f172a;
  font-size: 1.1rem;
  line-height: 1.1;
}
.header-actions {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}
.month-picker,
.week-picker,
.sort-picker {
  display: grid;
  gap: 4px;
  font-size: 0.82rem;
  font-weight: 600;
}
.month-picker input,
.week-picker select,
.sort-picker select {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px;
}
.sort-dir-btn {
  border: 1px solid #94a3b8;
  background: #fff;
  color: #0f172a;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 700;
}
.download-btn {
  border: 1px solid #0ea5e9;
  background: #0284c7;
  color: #fff;
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 700;
}
.attendance-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin: 8px 0 10px;
}
.attendance-tab-btn {
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1e3a8a;
  border-radius: 999px;
  padding: 6px 11px;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.82rem;
}
.attendance-tab-btn--active {
  border-color: #1d4ed8;
  background: #1d4ed8;
  color: #ffffff;
}
.table-wrap {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #dbe6f7;
  border-radius: 12px;
  overflow: auto;
  box-shadow: 0 20px 36px rgba(15, 23, 42, 0.09);
}
.matrix-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.matrix-table th,
.matrix-table td {
  border-bottom: 1px solid #edf2fb;
  padding: 6px;
  text-align: center;
}
.matrix-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fff;
  color: #0f172a;
  border-bottom: 1px solid #dbe6f7;
  border-right: 1px solid #edf2fb;
  font-weight: 700;
  font-size: 0.8rem;
}
.matrix-table thead th.day-header {
  color: #334155;
}
.matrix-table thead th.week-start-header {
  background: #eff6ff;
  color: #1e3a8a;
  box-shadow: inset 0 -2px 0 #60a5fa;
}
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 3;
  background: #f8fbff;
  text-align: left !important;
}
.sticky-name {
  min-width: 190px;
}
.name-wrap {
  display: grid;
  gap: 4px;
}
.employee-actions {
  margin-top: 4px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.employee-action-btn {
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  background: #fff;
  color: #1f2937;
  padding: 4px 8px;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  line-height: 1.1;
}
.employee-action-btn--vacation {
  border-color: #84cc16;
  color: #3f6212;
  background: #f7fee7;
}
.employee-action-btn--suspended {
  border-color: #fca5a5;
  color: #7f1d1d;
  background: #fef2f2;
}
.name-text {
  color: #0f172a;
  font-weight: 700;
}
.name-text--link {
  border: none;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  transition: color 0.15s ease;
}
.name-text--link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}
.cell-wrap {
  min-width: 128px;
}
.attendance-cell-combined {
  display: flex;
  align-items: stretch;
  gap: 6px;
}
.day-cell {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  min-width: 34px;
  height: 34px;
  padding: 0 4px;
  font-weight: 700;
  text-align: center;
}
.day-cell {
  cursor: pointer;
}
.hour-cell {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  min-width: 82px;
  min-height: 52px;
  padding: 5px 7px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  display: grid;
  gap: 3px;
}
.extra-cell {
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  min-width: 112px;
  min-height: 52px;
  padding: 5px 7px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  display: grid;
  gap: 3px;
}
.hour-line {
  color: #334155;
  font-size: 0.78rem;
  line-height: 1.25;
  white-space: nowrap;
}
.status-menu {
  display: grid;
  gap: 3px;
  min-width: 160px;
  background: #ffffff;
  border: 1px solid #d6deea;
  border-radius: 8px;
  padding: 4px;
  box-shadow: 0 14px 24px rgba(15, 23, 42, 0.18);
}
.status-menu--floating {
  z-index: 1100;
}
.status-menu__item {
  border: 1px solid #eef2f7;
  border-radius: 6px;
  padding: 5px 7px;
  text-align: left;
  font-weight: 600;
  cursor: pointer;
  background: #ffffff;
  display: flex;
  align-items: center;
  gap: 7px;
  color: #1f2937;
  font-size: 0.84rem;
}
.status-menu__item:hover {
  background: #f8fafc;
}
.status-menu__item--active {
  border-color: #1d4ed8;
  background: #eff6ff;
}
.status-menu__dot {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  border: 1px solid rgba(15, 23, 42, 0.12);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.8rem;
}
.hours-menu {
  display: grid;
  gap: 8px;
  min-width: 230px;
}
.hours-field {
  display: grid;
  gap: 4px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #334155;
}
.hours-field input {
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  padding: 6px 8px;
}
.hours-field--check {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}
.hours-field--check input {
  width: 16px;
  height: 16px;
  padding: 0;
}
.hours-field select {
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  padding: 6px 8px;
  background: #fff;
}
.hours-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}
.hours-save-btn {
  border-color: #1d4ed8;
  color: #1d4ed8;
}
.status-empty {
  background: #ffffff;
  color: #334155;
}
.status-a {
  background: #dcfce7;
  color: #166534;
}
.status-f {
  background: #fee2e2;
  color: #991b1b;
}
.status-r {
  background: #fef9c3;
  color: #854d0e;
}
.status-d {
  background: #dbeafe;
  color: #1e40af;
}
.status-t {
  background: #ede9fe;
  color: #5b21b6;
}
.status-s {
  background: #fecaca;
  color: #7f1d1d;
}
.status-v {
  background: #ecfccb;
  color: #3f6212;
}
.status-p {
  background: #cffafe;
  color: #0e7490;
}
.status-i {
  background: #fed7aa;
  color: #9a3412;
}
.week-start-col {
  background: rgba(239, 246, 255, 0.65);
}
.summary-table th,
.summary-table td {
  white-space: nowrap;
  min-width: 90px;
  font-size: 0.82rem;
}
.summary-table .sticky-name {
  min-width: 200px;
}
.summary-table tbody td {
  font-weight: 600;
  color: #1f2937;
}
.summary-table tbody td.summary-error {
  color: #dc2626;
  font-weight: 700;
}
.summary-input {
  width: 90px;
  border: 1px solid #cbd5e1;
  border-radius: 7px;
  padding: 5px 7px;
  font-weight: 600;
  text-align: right;
}
.summary-input:disabled {
  background: #f1f5f9;
  color: #94a3b8;
}
.summary-table tbody tr:nth-child(even) > td {
  background: #f6f9fd;
}
.summary-table thead th:nth-child(even),
.summary-table tbody td:nth-child(even) {
  background: #eef3fa;
}
.summary-table tbody tr:nth-child(even) > td:nth-child(even) {
  background: #e6edf7;
}
.summary-table tbody tr:hover > td {
  background: #dbeafe !important;
}
.summary-table tbody tr:hover > td.sticky-col {
  background: #c7ddfb !important;
}
.legend {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #fff;
  border: 1px solid #dbe6f7;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.82rem;
}
.legend-pill {
  min-width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(15, 23, 42, 0.12);
}
.empty-box,
.success-box,
.error-box {
  margin-top: 12px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 12px;
}
.success-box {
  color: #166534;
  border-color: #bbf7d0;
}
.error-box {
  color: #b91c1c;
  border-color: #fecaca;
}
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  backdrop-filter: blur(2px);
}
.modal-card {
  width: min(820px, 100%);
  max-height: 82vh;
  background: linear-gradient(180deg, #ffffff, #f8fbff);
  border-radius: 14px;
  border: 1px solid #d7e3f7;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 46px rgba(2, 6, 23, 0.25);
}
.modal-card--compact {
  width: min(460px, 100%);
}
.range-status-form {
  display: grid;
  gap: 10px;
  padding: 12px;
}
.range-status-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
.modal-head {
  padding: 12px;
  border-bottom: 1px solid #dbe7f7;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  background: linear-gradient(90deg, #eff6ff, #ecfeff);
}
.modal-head h3 {
  margin: 0;
  color: #0f172a;
}
.modal-subtitle {
  margin: 2px 0 0;
  font-size: 0.82rem;
  color: #475569;
}
.modal-actions {
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fbff;
}
.options-list {
  overflow: auto;
  padding: 12px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
}
.option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 10px;
  border: 1px solid #dbe5f3;
  border-radius: 10px;
  background: #fff;
}
.small-btn {
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  font-weight: 600;
}
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.logbook-card {
  width: min(980px, 100%);
  max-height: 86vh;
}
.logbook-state {
  padding: 28px 16px;
  text-align: center;
  color: #475569;
  font-weight: 600;
}
.logbook-state--error {
  color: #b91c1c;
}
.logbook-body {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 0;
  min-height: 0;
  flex: 1;
}
.logbook-list {
  overflow-y: auto;
  border-right: 1px solid #dbe7f7;
  padding: 10px;
  display: grid;
  gap: 8px;
  align-content: start;
  background: #f8fbff;
}
.logbook-entry {
  border: 1px solid #dbe5f3;
  border-radius: 10px;
  background: #fff;
  padding: 9px 10px;
  cursor: pointer;
  text-align: left;
  display: grid;
  gap: 5px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}
.logbook-entry:hover {
  border-color: #93c5fd;
  transform: translateY(-1px);
}
.logbook-entry--active {
  border-color: #1d4ed8;
  background: #eff6ff;
  box-shadow: 0 0 0 1px rgba(29, 78, 216, 0.25);
}
.logbook-entry__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.logbook-entry__date {
  font-weight: 700;
  font-size: 0.82rem;
  color: #0f172a;
}
.logbook-entry__times {
  display: flex;
  gap: 10px;
  font-size: 0.78rem;
  color: #334155;
}
.logbook-entry__source {
  justify-self: start;
  font-size: 0.7rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 2px 8px;
}
.logbook-entry__source--gps {
  background: #dcfce7;
  color: #166534;
}
.logbook-entry__source--device {
  background: #f1f5f9;
  color: #475569;
}
.logbook-load-more {
  border: 1px solid #bfdbfe;
  border-radius: 10px;
  background: #eff6ff;
  color: #1e3a8a;
  padding: 9px 10px;
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.15s ease;
}
.logbook-load-more:hover:not(:disabled) {
  background: #dbeafe;
}
.logbook-load-more:disabled {
  opacity: 0.65;
  cursor: default;
}
.logbook-list-end {
  margin: 2px 0 0;
  text-align: center;
  font-size: 0.72rem;
  color: #94a3b8;
  font-weight: 600;
}
.logbook-load-error {
  margin: 0;
  text-align: center;
  font-size: 0.75rem;
  color: #b91c1c;
  font-weight: 600;
}
.logbook-detail {
  padding: 14px;
  overflow-y: auto;
  display: grid;
  gap: 12px;
  align-content: start;
}
.logbook-detail__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.logbook-detail__head h4 {
  margin: 0;
  color: #0f172a;
}
.logbook-detail__status {
  font-size: 0.78rem;
  font-weight: 700;
  color: #1e3a8a;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  padding: 3px 10px;
}
.logbook-detail__times {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.logbook-time-card {
  border: 1px solid #dbe5f3;
  border-radius: 10px;
  background: #fff;
  padding: 10px 12px;
  display: grid;
  gap: 3px;
}
.logbook-time-card span {
  font-size: 0.72rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 700;
}
.logbook-time-card strong {
  font-size: 1.15rem;
  color: #0f172a;
}
.logbook-map {
  width: 100%;
  height: 320px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #dbe5f3;
}
.logbook-location-text {
  margin: 0;
  font-size: 0.78rem;
  color: #475569;
  word-break: break-word;
}
.logbook-no-map {
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  padding: 14px;
  display: grid;
  gap: 6px;
  background: #f8fafc;
}
.logbook-no-map__hint {
  margin: 0;
  font-size: 0.75rem;
  color: #94a3b8;
}
.logbook-device-box {
  border: 1px solid #fde68a;
  background: #fffbeb;
  border-radius: 10px;
  padding: 22px 16px;
  display: grid;
  justify-items: center;
  gap: 8px;
  text-align: center;
}
.logbook-device-box__icon {
  font-size: 1.8rem;
}
.logbook-device-box p {
  margin: 0;
  color: #78350f;
  font-weight: 600;
}
.logbook-pop-enter-active {
  transition: opacity 0.25s ease;
}
.logbook-pop-leave-active {
  transition: opacity 0.2s ease;
}
.logbook-pop-enter-active .logbook-card {
  transition: transform 0.32s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.28s ease;
}
.logbook-pop-leave-active .logbook-card {
  transition: transform 0.18s ease, opacity 0.18s ease;
}
.logbook-pop-enter-from,
.logbook-pop-leave-to {
  opacity: 0;
}
.logbook-pop-enter-from .logbook-card {
  transform: translateY(22px) scale(0.95);
  opacity: 0;
}
.logbook-pop-leave-to .logbook-card {
  transform: translateY(12px) scale(0.97);
  opacity: 0;
}
@media (max-width: 780px) {
  .logbook-body {
    grid-template-columns: 1fr;
  }
  .logbook-list {
    max-height: 34vh;
    border-right: none;
    border-bottom: 1px solid #dbe7f7;
  }
}
@media (max-width: 980px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
